// Library Methods
import { useCallback, useEffect, useState } from "react";

// Components
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import SnackbarMessage from "src/components/shared/alerts/SnackbarMessage/SnackbarMessage";
import ClinicExams from "src/components/containers/Patients/ClinicExams/ClinicExams";
import ClinicPatients from "src/components/containers/Patients/ClinicPatients/ClinicPatients";
import PatientDevices from "src/components/containers/Patients/PatientDevices/PatientDevices";
import LiveUpdateModal from "src/components/containers/shared/LiveUpdateModal/LiveUpdateModal";
// import PatientSidebar from "src/components/containers/Patients/PatientSidebar/PatientSidebar";
// import PatientFooter from "src/components/containers/Patients/PatientFooter/PatientFooter";
// import PatientProvider from "src/contexts/patients/PatientContext";

// Hooks
import { useExamsData } from "src/services/exams/hooks/useExamsData";
import { usePatientsData } from "src/services/patients/hooks/usePatientsData";
import useOnlineConnection from "src/hooks/connection/useOnlineConnection";

// Styles
import { PageContainerWrapper } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useDevicesContext } from "src/contexts/devices/DevicesContext";
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";
import { ExternalExamsProvider } from "src/contexts/exams/ExternalExamsContext";

// Types
import { PatientType } from "src/services/patients/types";

const Patients = () => {
  const theme = useTheme();
  const { open } = useLiveUpdateContext();
  const { snackbarInfo, setSnackbarInfo } = useSnackbarContext();
  const { clinicInfo } = useClinicContext();
  const { setDevices } = useDevicesContext();

  const [selectedPatient, setSelectedPatient] = useState<PatientType>();

  const { refetch: refetchExams } = useExamsData(
    clinicInfo?.id,
    selectedPatient?.id
  );

  const refetchExamsWrapper = useCallback(async () => {
    await refetchExams();
  }, [refetchExams]);

  const {
    data: patientsData,
    isLoading: isLoadingPatients,
    isError: isErrorPatients,
  } = usePatientsData(clinicInfo?.id);

  // Sync selectedPatient with the latest patientsData (ex: after a lens update)
  useEffect(() => {
    if (!selectedPatient || !patientsData) return;

    const updatedPatient = patientsData.find(
      (patient) => patient.id === selectedPatient.id
    );
    if (updatedPatient) {
      setSelectedPatient(updatedPatient);
    }
  }, [patientsData, selectedPatient]);

  // Handle updating the device array based on signalR data
  const handleDeviceListUpdated = useCallback(
    (data: string) => {
      const parsedData = JSON.parse(data);
      setDevices([...parsedData?.data]);
    },
    [setDevices]
  );

  // signalR setup + online status
  const { connection, isLoadingSignalR } = useOnlineConnection({
    clinicId: clinicInfo?.id,
    handleDeviceListUpdated,
  });

  useEffect(() => {
    if (!open) {
      connection?.off("DeviceExamSubmitted");
      connection?.on("DeviceExamSubmitted", function () {
        refetchExamsWrapper();
      });
    }
  }, [open, connection, refetchExamsWrapper]);

  useEffect(() => {
    connection?.off("ClinicLensesUpdated");
    connection?.on("ClinicLensesUpdated", function () {
      refetchExamsWrapper();
    });
  }, [connection, refetchExamsWrapper]);

  return (
    <Box
      sx={{
        ...PageContainerWrapper(theme),
        padding: "0px",
        flexDirection: "column",
      }}
    >
      {/* <PatientProvider
        patients={patientsData}
        selectedPatient={selectedPatient}
        setSelectedPatient={setSelectedPatient}
        isLoading={isLoadingPatients}
        isError={isErrorPatients}
      >
        <Box
          sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column" }}
        >
          <PatientFooter />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              flexDirection: "column",
              flex: 1,
              display: { xs: "none", md: "flex" },
              borderRight: { xs: "none", md: "1px solid #5B6D7A40" },
              minWidth: "350px",
              maxWidth: "350px",
              minHeight: "100%",
            }}
          >
            <PatientSidebar />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minWidth: 0,
              padding: "16px",
              minHeight: "100%",
            }}
          >
            <ClinicExams selectedPatient={selectedPatient} />
          </Box>
        </Box>
      </PatientProvider> */}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            borderRight: {
              xs: "none",
              md:
                theme.palette.mode === "dark"
                  ? "2px solid #4D5063"
                  : "2px solid #5B6D7A40",
            },
            padding: "16px",
            maxWidth: { xs: "100%", md: "40%" },
            minHeight: "100%",
          }}
        >
          <ClinicPatients
            patients={patientsData}
            selectedPatient={selectedPatient}
            setSelectedPatient={setSelectedPatient}
            isLoading={isLoadingPatients}
            isError={isErrorPatients}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            padding: "16px",
            maxWidth: { xs: "100%", md: "60%" },
            minHeight: "100%",
            gap: "20px",
          }}
        >
          {selectedPatient && (
            <ExternalExamsProvider selectedPatient={selectedPatient}>
              <ClinicExams selectedPatient={selectedPatient} />
            </ExternalExamsProvider>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          padding: "16px",
          maxWidth: "100%",
          backgroundColor:
            theme.palette.mode === "dark" ? "#4D5063" : "#5B6D7A40",
          alignContent: "center",
        }}
      >
        <PatientDevices />
      </Box>

      {/* Snackbar success and error messages */}
      <SnackbarMessage
        snackbarInfo={snackbarInfo}
        setSnackbarInfo={setSnackbarInfo}
      />

      <LiveUpdateModal
        isLoadingSignalR={isLoadingSignalR}
        connection={connection}
        refetchExams={refetchExamsWrapper}
      />
    </Box>
  );
};

export default Patients;
